import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import {
  convertCommunicationIdToElementDesignId,
  convertEquipmentNumberToCommunicationId,
  filterUndefined,
  isDesignId,
} from 'src/app/lego-utils';
import { NzMessageService } from 'ng-zorro-antd/message';
import { asyncScheduler, from, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, observeOn, switchMap } from 'rxjs/operators';
import { DesignService, SessionService } from 'src/app/state';

type SelectedSearch = 'Design' | 'Equipment';

@Component({
  selector: 'em-design-lookup-search-field',
  styleUrls: ['./design-lookup-search-field.component.less'],
  templateUrl: './design-lookup-search-field.component.html',
  standalone: false,
})
export class DesignLookupSearchFieldComponent implements OnInit, OnDestroy {
  public role$ = this.sessionService.role$;
  public searchForm: FormGroup<{
    designInput: FormControl<string | undefined>;
  }> = this.fb.group({
    designInput: this.fb.control<string | undefined>(undefined, { nonNullable: true }),
  });

  public selectValue: SelectedSearch = 'Design';

  private readonly subscriptions = new Subscription();

  public constructor(
    private readonly designService: DesignService,
    private readonly fb: FormBuilder,
    private readonly sessionService: SessionService,
    private readonly message: NzMessageService,
    private readonly router: Router
  ) {}

  public ngOnInit(): void {
    this.subscriptions.add(
      this.designService.query$
        .pipe(
          distinctUntilChanged(
            (prev, next) =>
              prev.designId === next.designId && prev.equipmentNumber === next.equipmentNumber
          ),
          map(({ designId }) => {
            const control = this.searchForm.controls.designInput;
            control.setValue(designId);

            if (!designId) {
              return '';
            }

            return isDesignId(designId)
              ? designId
              : convertCommunicationIdToElementDesignId(designId);
          }),
          filter((designId) => {
            const design = this.designService.getDesign();

            return !design || design.designId !== designId;
          }),
          // make async to remove the ExpressionChangedAfterItHasBeenChecked error
          observeOn(asyncScheduler),
          switchMap((designId) => this.designService.loadByDesignId(designId)),
          switchMap(() => this.designService.design$)
        )
        .subscribe((design) => {
          if (design) {
            return;
          }

          const query = this.designService.getQuery();

          if (!query) {
            this.message.create('info', 'Could not find design');

            return;
          }

          if (query.equipmentNumber) {
            this.message.create(
              'info',
              `Couldn't find a design for equipment with id: ${query.equipmentNumber}`
            );

            return;
          }

          this.message.create('info', `Couldn't find a design with id: ${query.designId}`);
        })
    );

    this.subscriptions.add(
      this.designService.error$.pipe(filterUndefined()).subscribe((error) => {
        this.message.create('error', error);
      })
    );
  }

  public lookup() {
    this.selectValue === 'Design'
      ? this.lookupDesignByDesignTypeId()
      : this.lookupDesignByEquipmentTypeId();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private lookupDesignByEquipmentTypeId() {
    const equipmentNumber = this.searchForm.controls.designInput.value as string;
    const communicationId = convertEquipmentNumberToCommunicationId(equipmentNumber);
    const designId = convertCommunicationIdToElementDesignId(communicationId);

    this.subscriptions.add(
      from(
        this.router.navigate(['data', 'design', designId], {
          queryParams: { tab: 'general', equipmentNumber },
        })
      ).subscribe()
    );
  }

  private lookupDesignByDesignTypeId() {
    const id = this.searchForm.controls.designInput.value;

    if (!id) {
      this.message.create('warning', 'Please enter a DesignId');

      return;
    }

    const designId = isDesignId(id) ? id : convertCommunicationIdToElementDesignId(id);

    this.subscriptions.add(
      from(
        this.router.navigate(['data', 'design', designId], { queryParams: { tab: 'general' } })
      ).subscribe()
    );
  }
}
